import { Button } from '@advanza/shadcn-ui/src/components/ui/button'
import { Calendar } from '@advanza/shadcn-ui/src/components/ui/calendar'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@advanza/shadcn-ui/src/components/ui/popover'
import { cn } from '@advanza/shadcn-ui/src/lib/utils'
import { format, isValid, parseISO, setHours, startOfDay } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import { DateRange } from 'react-day-picker'

interface Props {
    className?: string
    onChangeValue: (date: DateRange | undefined) => {} | void
    value: DateRange | undefined
}

export function PopUpNotificationDateRangePicker({ className = '', onChangeValue, value }: Props) {
    // Convert dates to noon UTC to avoid timezone issues
    function toUTCNoon(date: Date | undefined): Date | undefined {
        if (!date) return undefined
        const localDate = startOfDay(date)
        return setHours(localDate, 12)
    }

    const parsedDate = {
        from: value?.from ? toUTCNoon(new Date(value.from)) : undefined,
        to: value?.to ? toUTCNoon(new Date(value.to)) : undefined,
    }
    const [date, setDate] = useState<DateRange | undefined>(parsedDate)

    function toDateSafe(dateLike: Date | string) {
        if (dateLike instanceof Date) return toUTCNoon(dateLike)
        return toUTCNoon(parseISO(dateLike))
    }

    return (
        <div className={cn('grid gap-2 z-40 bg-grey', className)}>
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        id="date"
                        variant={'outline'}
                        className={cn(
                            'w-full justify-start text-left font-normal bg-white border border-grey-200',
                            !date && 'text-muted-foreground',
                        )}>
                        <CalendarIcon />
                        {(() => {
                            if (!date?.from) {
                                return <span>Always show once</span>
                            }
                            if (date.to) {
                                if (
                                    isValid(toDateSafe(date.from)) &&
                                    isValid(toDateSafe(date.to))
                                ) {
                                    return (
                                        <>
                                            {format(toDateSafe(date.from)!, 'LLL dd, y')} -{' '}
                                            {format(toDateSafe(date.to)!, 'LLL dd, y')}
                                        </>
                                    )
                                }
                                return <span>Invalid Date</span>
                            }
                            if (isValid(date.from)) {
                                return format(toDateSafe(date.from)!, 'LLL dd, y')
                            }
                            return <span>Invalid Date</span>
                        })()}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                    <Calendar
                        style={{ backgroundColor: 'white' }}
                        initialFocus
                        mode="range"
                        defaultMonth={parsedDate?.from}
                        selected={parsedDate}
                        onSelect={(selectedDate) => {
                            if (selectedDate) {
                                const normalizedDate = {
                                    from: selectedDate.from
                                        ? toUTCNoon(selectedDate.from)
                                        : undefined,
                                    to: selectedDate.to ? toUTCNoon(selectedDate.to) : undefined,
                                }
                                setDate(normalizedDate)
                                onChangeValue(normalizedDate)
                            } else {
                                setDate(undefined)
                                onChangeValue(undefined)
                            }
                        }}
                        numberOfMonths={2}
                    />
                </PopoverContent>
            </Popover>
        </div>
    )
}
